import { route } from "preact-router";
import { Inputs, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Command } from "./Command";

const emptyArray = [] as unknown[];

export function useBind<T, TRet>(callback: (arg: T) => TRet, arg: T) {
    const callbackRef = useRef<{ callback: (arg: T) => TRet, boundCallback: () => TRet, arg: T } | null>(null);

    if (!callbackRef.current) {
        callbackRef.current = {
            callback,
            boundCallback: () => callbackRef.current!.callback(callbackRef.current!.arg),
            arg
        };
    }

    callbackRef.current.callback = callback
    callbackRef.current.arg = arg;

    return callbackRef.current.boundCallback;
}

export function useBind2<T, T2, TRet>(callback: (arg: T, arg2: T2) => TRet, arg: T, arg2: T2) {
    const callbackRef = useRef<{ callback: (arg: T, arg2: T2) => TRet, boundCallback: () => TRet, arg: T, arg2: T2 } | null>(null);

    if (!callbackRef.current) {
        callbackRef.current = {
            callback,
            boundCallback: () => callbackRef.current!.callback(callbackRef.current!.arg, callbackRef.current!.arg2),
            arg, arg2
        };
    }

    callbackRef.current.callback = callback
    callbackRef.current.arg = arg;
    callbackRef.current.arg2 = arg2;

    return callbackRef.current.boundCallback;
}

function incrementValue(v: number) {
    return v + 1;
}

function incrementStateValue(setter: (prev: (v: number) => number) => void) {
    setter(incrementValue);
}

export function useForceUpdate(){
    return useBind(incrementStateValue, useState(0)[1]); // update the state to force render
}

export function useDelay() {


    const state = useMemo(() => ({ timeout: null as number | null }), [true]);

    return [(time: number, handler: () => void) => {
        if (state.timeout) {
            clearTimeout(state.timeout);
            state.timeout = null;
        }

        state.timeout = window.setTimeout(handler, time);
    }, () => {
        if (state.timeout) {
            clearTimeout(state.timeout);
            state.timeout = null;
        }
    }] as const;
}

export function useTimer(callback: () => void, time: number, inputs: Inputs) {
    const state = useMemo(() => ({ interval: null as number | null }), [true]);

    useEffect(() => {
        if (state.interval !== null) {
            clearInterval(state.interval);
            state.interval = null;
        }

        state.interval = window.setInterval(callback, time);

        return () => {
            if (state.interval !== null) {
                clearInterval(state.interval);
                state.interval = null;
            }
        }
    }, inputs);
}

export function useTimeout(callback: (repeat: () => void) => void, time: number, inputs: Inputs) {
    const state = useMemo(() => ({ timeout: null as number | null }), [true]);

    useEffect(() => {
        if (state.timeout !== null) {
            clearTimeout(state.timeout);
            state.timeout = null;
        }

        const repeat = () => {
            if (state.timeout !== null) {
                state.timeout = window.setTimeout(callback, time, repeat);
            }
        };

        state.timeout = window.setTimeout(callback, time, repeat);

        return () => {
            if (state.timeout !== null) {
                clearTimeout(state.timeout);
                state.timeout = null;
            }
        }
    }, inputs);
}

function commandCreator() {
    return new Command<any>();
}

export function useCommand<T extends unknown[]>(): Command<T> {
    const command = useMemo(commandCreator, emptyArray);
    return command;
}

export function useCommandHandler<T extends unknown[]>(command: Command<T> | null | undefined, handler: (...args: [...T]) => void) {
    if (!command) {
        return;
    }

    command.handler = handler;
}

export function useEvent<T extends (...args: unknown[]) => unknown>(event: T[], callback: T) {
    useEffect(() => {
        event.push(callback);

        return () => {
            const index = event.indexOf(callback);

            if (index >= 0) {
                event.splice(index, 1);
            }
        };
    }, [event, callback]);
}

interface EventSourceCallbacks<T> {
    onMessage?: (data: string, userdata: T) => unknown;
    onClose?: (reconnect: (maxAttempts: number) => void, userdata: T) => unknown;
    onError?: (error: Event, reconnect: (maxAttempts: number) => void, userdata: T) => unknown;
    onOpen?: (userdata: T) => unknown;
    onFatal?: (userdata: T) => void;
    customEvents?: {
        [key: string]: (data: string | undefined, userdata: T) => unknown;
    };
}

export function useEventSource<T extends object = { [key: string]: any }>(
    url: string,
    condition: boolean | (() => boolean),
    callback: ((data: string) => unknown) | EventSourceCallbacks<T>,
    inputs: Inputs = []
) {
    const state = useMemo(() => ({
        source: null as EventSource | null,
        callback,
        reconnectCount: 0,
        hasOpened: false,
        userdata: {} as T
    }), [true]);
    const [reconnect, setReconnect] = useState(0);

    useEffect(() => {
        state.callback = callback;
    }, inputs);

    useEffect(() => {
        const connect = typeof condition === "function" ? condition() : condition;
        if (!connect && state.source !== null) {
            state.source.close();
            state.source = null;
        }
    }, [condition]);

    useEffect(() => {
        const connect = typeof condition === "function" ? condition() : condition;

        if (!connect) {
            return;
        }

        if (state.source !== null) {
            state.source.close();
            state.source = null;
        }

        state.source = new EventSource(url);
        state.source.onmessage = (e) => {
            if(typeof state.callback === "function") {
                state.callback(e.data);
            }
            else {
                if (state.callback.onMessage) {
                    state.callback.onMessage(e.data, state.userdata);
                }
            }
        };

        state.source.onopen = (e) => {
            state.hasOpened = true;
            state.reconnectCount = 0;

            if(typeof state.callback !== "function") {
                if (state.callback.onOpen) {
                    state.callback.onOpen(state.userdata);
                }
            }
        };

        state.source.onerror = (e) => {
            if(typeof state.callback === "function") {
                return;
            }

            const reconnectFunc = (maxAttempts: number) => {
                if (state.reconnectCount < maxAttempts) {
                    setReconnect(reconnect + 1);
                    state.reconnectCount++;
                }
                else if(typeof state.callback !== "function" && state.callback.onFatal) {
                    state.callback.onFatal(state.userdata);
                }
            };

            if (state.source && e.eventPhase == EventSource.CLOSED && state.hasOpened && state.callback.onClose) {
                state.source.close();
                state.source = null;

                state.callback.onClose(reconnectFunc, state.userdata)
                return;
            }

            if (state.callback.onError) {
                state.callback.onError(e, reconnectFunc, state.userdata);
            }
        };

        if (typeof state.callback === "object" && state.callback.customEvents) {
            for (const prop in state.callback.customEvents) {
                const callback = state.callback.customEvents[prop];

                state.source.addEventListener(prop, (e) => {
                    callback((e as MessageEvent<any>).data, state.userdata);
                });
            }
        }

        return () => {
            if (state.source !== null) {
                state.source.close();
                state.source = null;
            }
        }
    }, [url, reconnect, condition]);
}

function emptyObjectCreator() {
    return { current: null as any };
}

export function useDepRef<T>(inputs?: readonly unknown[]): { current?: T | null } {
    return useMemo(emptyObjectCreator, inputs);
}

function memoObjectCreator() {
    return { current: true as boolean };
}

export function useInputs(inputs: Inputs) {
    const value = useMemo(memoObjectCreator, inputs);
    const result = value.current;
    value.current = false;
    return result;
}

export function useCheckMemo<T>(instance: T | false): T {
    const v = useMemo(emptyObjectCreator as () => { current: T }, []);

    if (!instance) {
        return v.current as T;
    }

    v.current = instance;
    return instance as T;
}

let inlineMemoValue = null as { current: any } | null;

export function useInlineMemo<T>(inputs: Inputs): T | undefined {
    const value = useMemo(emptyObjectCreator, inputs);
    inlineMemoValue = value;
    return value.current;
}


export function useValue<T>(value: T) {
    if (inlineMemoValue) {
        inlineMemoValue.current = value;
    }

    const result = inlineMemoValue?.current;
    inlineMemoValue = null;
    return result;
}

function emptyLazyRefCreator(factory: () => any) {
    return {
        _current: null as any,

        get current() {
            if (this._current === null) {
                this._current = factory();
            }

            return this._current;
        }
    }
}

export function useLazyRef<T>(factory: () => T, inputs?: readonly unknown[]): { current: T } {
    const boundFactory = useBind(emptyLazyRefCreator, factory);
    const ref = useMemo(boundFactory, inputs);

    return ref;
}

export function useStepperIndex() {
    let index = 0;

    return () => {
        index++;
        return index;
    };
}

export function useRouteState(id: string, defualtValue: string) {
    const params = new URLSearchParams(location.search);

    return [
        params.get(id) ?? defualtValue,
        (v: string, replace?: boolean) => {
            params.set(id, v);
            const url = new URL(location.href);
            const oldHref = url.href;

            if (!v) {
                url.searchParams.delete(id);
            }
            else {
                url.searchParams.set(id, v);
            }

            if (oldHref === url.href) {
                return;
            }

            route(url.pathname + url.search, replace);
        }
    ] as const;
}
